// Making things from nothing in the room: a new instrument track (a MIDI lane
// plus an empty clip, opened in the piano roll — GarageBand's flow), a new
// audio track, and a new MIDI clip on a lane. The room names what it makes,
// so "open the new clip" waits until the room shows it: a connected room
// answers a round trip later, and a refused create never shows at all.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { InstrumentId } from "@shared/instruments.js";
import type { CanonicalRoom } from "@shared/sync/state.js";
import { displayNameOf } from "@shared/sync/track-id.js";
import { createMidiClip, createTrack, newInstrumentLane, nextOrd } from "@web-shared/lib/edits";
import { instrumentEntry, previewPhrase } from "@web-shared/lib/instrument-catalog";
import { firstFreeSlot } from "@web-shared/lib/placement";
import { patchById } from "@web-shared/lib/synth";
import type { ArrangementPlayerApi } from "@web-shared/hooks/useArrangementPlayer";
import type { LocalEditor } from "./useLocalEditor";

/** A new clip's length: four bars of 4/4. */
export const NEW_CLIP_BEATS = 16;
/** How long to wait for the room to show a create before giving up on opening it. */
const PENDING_MS = 8000;

type Pending =
  | { kind: "lane"; tracksBefore: ReadonlySet<string>; at: number }
  | { kind: "audio"; tracksBefore: ReadonlySet<string>; at: number }
  | { kind: "clip"; laneId: string; clipsBefore: ReadonlySet<string>; at: number };

/** `base`, or `base 2`, `base 3`… — the first a lane isn't already called. */
export function freshTrackName(room: CanonicalRoom, base: string): string {
  const taken = new Set(room.tracks.map((t) => t.name));
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) if (!taken.has(`${base} ${n}`)) return `${base} ${n}`;
}

export function useTrackCreation({
  room,
  editor,
  player,
  insertBeats,
  onOpen,
  onAudioLane,
}: {
  room: CanonicalRoom | null;
  editor: LocalEditor;
  player: ArrangementPlayerApi;
  /** Where new things land: Live's insert marker, in beats. */
  insertBeats: () => number;
  /** Open a clip in its editor. */
  onOpen: (clipId: string) => void;
  /** A new audio track landed: select it (where a take, a loop lands). */
  onAudioLane: (trackId: string) => void;
}) {
  const [pending, setPending] = useState<Pending | null>(null);
  const roomRef = useRef(room);
  roomRef.current = room;

  useEffect(() => {
    if (!pending || !room) return;
    if (Date.now() - pending.at > PENDING_MS) {
      setPending(null);
      return;
    }
    if (pending.kind === "audio") {
      const lane = room.tracks.find((t) => !pending.tracksBefore.has(t.id) && (t.trackKind ?? "audio") === "audio");
      if (!lane) return;
      setPending(null);
      onAudioLane(lane.id);
    } else if (pending.kind === "lane") {
      const lane = room.tracks.find((t) => !pending.tracksBefore.has(t.id) && t.trackKind === "midi");
      const clip = lane && room.clips.find((c) => c.trackId === lane.id);
      if (!clip) return;
      setPending(null);
      onOpen(clip.id);
    } else {
      const clip = room.clips.find((c) => c.trackId === pending.laneId && !pending.clipsBefore.has(c.id));
      if (!clip) return;
      setPending(null);
      onOpen(clip.id);
    }
  }, [room, pending, onOpen, onAudioLane]);

  /** The insert marker, on the bar it is in. */
  const barAtInsert = useCallback(() => Math.max(0, Math.floor(insertBeats() / 4) * 4), [insertBeats]);

  const addInstrument = useCallback(
    (id: InstrumentId): boolean => {
      const now = roomRef.current;
      if (!now) return false;
      const name = freshTrackName(now, instrumentEntry(id)?.trackName ?? "MIDI");
      const made = newInstrumentLane(name, nextOrd(now), id, barAtInsert(), NEW_CLIP_BEATS);
      if (!made) return false;
      const tracksBefore = new Set(now.tracks.map((t) => t.id));
      if (!editor.propose(made.intents)) return false;
      setPending({ kind: "lane", tracksBefore, at: Date.now() });
      return true;
    },
    [editor, barAtInsert],
  );

  /** A new audio track, selected once it lands. */
  const addAudio = useCallback((): boolean => {
    const now = roomRef.current;
    if (!now) return false;
    const tracksBefore = new Set(now.tracks.map((t) => t.id));
    if (!editor.propose([createTrack(freshTrackName(now, "Audio"), nextOrd(now), "audio")])) return false;
    setPending({ kind: "audio", tracksBefore, at: Date.now() });
    return true;
  }, [editor]);

  /** An empty MIDI clip on a lane at `atBeat` (the next gap that fits), opened. */
  const addMidiClip = useCallback(
    (laneId: string, atBeat?: number): boolean => {
      const now = roomRef.current;
      if (!now) return false;
      const at = atBeat ?? barAtInsert();
      const lane = now.tracks.find((t) => t.id === laneId);
      // The track's name as read, not its lane token (⟨sb:…⟩).
      const name = lane ? displayNameOf(lane.name) : undefined;
      const intent = createMidiClip(now, laneId, Math.max(0, at), NEW_CLIP_BEATS, (s, d) => firstFreeSlot(now, laneId, s, d), { name });
      if (!intent) return false;
      const clipsBefore = new Set(now.clips.filter((c) => c.trackId === laneId).map((c) => c.id));
      if (!editor.propose([intent])) return false;
      setPending({ kind: "clip", laneId, clipsBefore, at: Date.now() });
      return true;
    },
    [editor, barAtInsert],
  );

  // Hear an instrument; the same tap while it sounds stops it.
  const { previewPatch, stopAudition, auditioningId } = player;
  const previewing = useMemo<InstrumentId | null>(() => {
    const id = auditioningId?.startsWith("preview:") ? auditioningId.slice("preview:".length) : null;
    return id && instrumentEntry(id as InstrumentId) ? (id as InstrumentId) : null;
  }, [auditioningId]);
  const preview = useCallback(
    (id: InstrumentId) => {
      if (previewing === id) stopAudition();
      else previewPatch(patchById(id), previewPhrase(id));
    },
    [previewPatch, stopAudition, previewing],
  );

  return { addInstrument, addAudio, addMidiClip, preview, previewing };
}
